import { entityTypes } from '@/enums/generic';
import { OverallRadioCheckStatus } from '@/enums/radiocheck';
import { generateUniqueId } from '@/helpers/uuidUtils';
import { deviceTypes } from '@/enums/device';
import {
  DeviceType,
  FailedInstallationAttempt,
} from '@/models/installationPoint/InstallationPoint';
import { LoRaWanRadioCheck } from '@/api/funkcheck/LoRaWanRadioCheck';
import { CellularConnectivityQuality } from '@/api/connectivity/ConnectivityApi';
import { DeviceClasses } from '@/models/devices/Device';
import { PlantDataClasses } from '@/models/plant/Plant';
import { useEntitiesStore } from '@/store/entities/entitiesStore';
import { Type } from 'class-transformer';
import { TagCustomStyle } from '@/utils/canvas/drawingUtils';
import {
  Adapter,
  Accessory,
  BatteryPack,
} from '@/models/installationPoint/Accessory';

export enum InspectionPointType {
  SRT_ADAPTER = 'SRT_ADAPTER',
}

export enum InspectionPointLabel {
  ADAPTER_SRT = 'Adapter (SRT)',
  TEST_SRT = 'Test-SRT',
  GATEWAY = 'Gateway',
}

export class InspectionPoint {
  id: string;
  type = entityTypes.inspectionPoint;
  roomId: string;
  activeTestDeviceId?: string;
  @Type(() => InspectionData)
  inspectionData: InspectionData;
  @Type(() => Accessory, {
    keepDiscriminatorProperty: true,
    discriminator: {
      property: 'type',
      subTypes: [
        { value: Adapter, name: 'SRT_ADAPTER' },
        { value: Adapter, name: 'GATEWAY_HOUSING' },
        { value: BatteryPack, name: 'SRT_BATTERY_PACK' },
      ],
    },
  })
  accessories: Adapter[];
  supportedDeviceType?: DeviceType;
  failedInstallationAttempt?: FailedInstallationAttempt;
  note?: string;
  isRemoteTest: boolean;

  constructor(roomId: string) {
    this.id = generateUniqueId();
    this.roomId = roomId;
    this.note = undefined;
    this.accessories = [];
    this.activeTestDeviceId = undefined;
    this.inspectionData = new InspectionData();
    this.failedInstallationAttempt = undefined;
    this.supportedDeviceType = undefined;
    this.isRemoteTest = false;
  }

  isInspectionPointTypeAdapter() {
    return (
      this.inspectionData.inspectionType === InspectionPointType.SRT_ADAPTER
    );
  }

  setSupportedDeviceType(deviceType: DeviceType) {
    this.supportedDeviceType = deviceType;
  }

  addInspectionData(inspectionData: InspectionData) {
    this.inspectionData = inspectionData;
  }

  getActiveDevice(): DeviceClasses | PlantDataClasses {
    if (!this.activeTestDeviceId) throw Error('No active device id');
    return useEntitiesStore().getEntityById(this.activeTestDeviceId);
  }

  unsetActiveDevice() {
    this.activeTestDeviceId = undefined;
  }

  getParentId() {
    return this.roomId;
  }

  getLabel() {
    const isSRT =
      this.supportedDeviceType === deviceTypes.SmartRadiatorThermostat;
    const isInspectionTypeAdapter = this.isInspectionPointTypeAdapter();

    if (isInspectionTypeAdapter && this.failedInstallationAttempt) {
      return InspectionPointLabel.ADAPTER_SRT;
    }

    if (isInspectionTypeAdapter && this.activeTestDeviceId && isSRT) {
      return InspectionPointLabel.TEST_SRT;
    }

    if (isInspectionTypeAdapter) {
      return InspectionPointLabel.ADAPTER_SRT;
    }

    if (isSRT) {
      return InspectionPointLabel.TEST_SRT;
    }

    return InspectionPointLabel.GATEWAY;
  }

  getTagColor() {
    if (this.isInspectionPointTypeAdapter()) {
      if (
        this.activeTestDeviceId &&
        this.supportedDeviceType === deviceTypes.SmartRadiatorThermostat
      ) {
        return '#7c3aed';
      }
      return '#361E7A';
    }
    if (this.supportedDeviceType === deviceTypes.SmartRadiatorThermostat) {
      return '#7c3aed';
    }
    if (this.supportedDeviceType === deviceTypes.LoRaWanGateway) {
      return '#22c55e';
    }

    return '#7c3aed';
  }

  _getGatewayStyling(inspectionPoints: OverallRadioCheckStatus[]) {
    if (inspectionPoints.includes(OverallRadioCheckStatus.COMPLETED)) {
      return {
        borderColor: 'border-green-600 dark:border-green-600',
        backgroundColor: 'bg-green-50 dark:bg-green-950',
        textColor: 'text-zinc-900 dark:text-white',
        iconClass: 'mdi-router-wireless',
        testId: 'gateway-status-badge',
      };
    } else if (inspectionPoints.includes(OverallRadioCheckStatus.FAILED)) {
      return {
        borderColor: 'border-red-600 dark:border-red-600',
        backgroundColor: 'bg-red-50 dark:bg-red-950',
        textColor: 'text-zinc-900 dark:text-white',
        iconClass: 'mdi-router-wireless',
        testId: 'gateway-status-badge',
      };
    } else if (inspectionPoints.includes(OverallRadioCheckStatus.CANCELED)) {
      return {
        borderColor: 'border-yellow-600 dark:border-yellow-600',
        backgroundColor: 'bg-yellow-50 dark:bg-yellow-950',
        textColor: 'text-zinc-900 dark:text-white',
        iconClass: 'mdi-router-wireless',
        testId: 'gateway-status-badge',
      };
    }
    return {
      borderColor: '',
      backgroundColor: '',
      textColor: 'text-zinc-900 border-zinc-300 dark:text-white',
      iconClass: 'mdi-router-wireless',
      testId: 'gateway-status-badge',
    };
  }

  getAdvancedTagStyling(
    inspectionPoints?: OverallRadioCheckStatus[]
  ): TagCustomStyle | undefined {
    if (this.failedInstallationAttempt) {
      return {
        borderColor: '#B69FF7',
        backgroundColor: '#361E7A',
        textColor: 'text-white dark:text-white',
        testId: 'failed-installation-badge',
      };
    }

    if (
      this.inspectionData &&
      this.inspectionData.inspectionType === InspectionPointType.SRT_ADAPTER &&
      !this.activeTestDeviceId
    ) {
      return {
        borderColor: '#B69FF7',
        backgroundColor: '#361E7A',
        textColor: 'text-zinc-200',
      };
    }

    if (
      this.supportedDeviceType === deviceTypes.LoRaWanGateway &&
      inspectionPoints
    ) {
      return this._getGatewayStyling(inspectionPoints);
    }
    return undefined;
  }
}

export class InspectionData {
  lteReception?: number;
  lteReceptionDbm?: number;
  lteReceptionQuality?: CellularConnectivityQuality;
  lteReceptionTimestamp: number | null;
  isLteDeviceAvailable?: boolean;
  hasMobileLteConnection?: boolean;
  radioCheckResult?: OverallRadioCheckStatus;
  radioChecks: Array<LoRaWanRadioCheck>;
  connectionOptions: Array<string>;
  connectionAvailable: boolean;
  cableLength?: number;
  counterPressure?: number;
  noAdapterNeeded: boolean;
  inspectionType?: InspectionPointType;
  isInstallationLocation?: boolean;

  constructor() {
    this.lteReception = undefined;
    this.lteReceptionDbm = undefined;
    this.lteReceptionQuality = undefined;
    this.lteReceptionTimestamp = null;
    this.isLteDeviceAvailable = true;
    this.hasMobileLteConnection = undefined;
    this.radioCheckResult = undefined;
    this.radioChecks = [];
    this.connectionOptions = [];
    this.connectionAvailable = true;
    this.cableLength = undefined;
    this.counterPressure = undefined;
    this.noAdapterNeeded = false;
    this.inspectionType = undefined;
    this.isInstallationLocation = false;
  }

  setRadioCheckResult(
    result: LoRaWanRadioCheck,
    runId?: number,
    startedAt?: string,
    updatedAt?: string
  ) {
    const radioCheckResult = {
      status: result.status,
      runId,
      startedAt,
      updatedAt,
      result: result.result,
    };
    this.radioCheckResult = result.status;
    this.radioChecks.push(radioCheckResult);
  }

  setIsInstallationLocation(isInstallationLocation: boolean) {
    this.isInstallationLocation = isInstallationLocation;
  }
}
